// Dismissing the "We Are Hiring" banner hides it for a week, not for good.
//
// The banner is shown by the CAREERS_OPEN switch, which is turned on once or
// twice a year for about a month at a time. A permanent dismissal would
// silence a whole campaign for anyone who closed the banner on day one, and
// would silence every future campaign for them too. A week means a dismisser
// sees it roughly four times across a month-long run.
//
// Before #1080 the dismissal wrote an expiry and the read only checked that a
// value existed, so the expiry never took effect and every dismissal was
// permanent. Values written by that version carry an expiry in the past and so
// read as expired here, which is the behaviour we want.

const STORAGE_KEY = "dismissHiringSection";

// Change this one value to make the banner return sooner or later.
const DISMISSAL_DAYS = 7;
const MAX_AGE_MS = DISMISSAL_DAYS * 24 * 60 * 60 * 1000;

type StoredDismissal = {
  expiry: number;
};

// True while a dismissal is still inside its week.
//
// Anything we cannot read or cannot parse counts as "not dismissed" so that a
// corrupt or foreign value can never hide the banner permanently, which is the
// fault being fixed. Storage access itself is guarded because a browser with
// web storage switched off has `window.localStorage` as null or throwing
// (#871).
export const isDismissalStanding = (now: number = Date.now()): boolean => {
  let raw: string | null = null;

  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    // Storage disabled — show the banner.
    return false;
  }

  if (!raw) return false;

  let stored: StoredDismissal;
  try {
    stored = JSON.parse(raw);
  } catch (e) {
    // Not JSON — show the banner.
    return false;
  }

  if (!stored || typeof stored.expiry !== "number") return false;

  return now < stored.expiry;
};

// Hide the banner for the next week. A failure to write is not worth failing
// the click for: the banner returns on the next page load instead.
export const recordDismissal = (now: number = Date.now()): void => {
  const record: StoredDismissal = { expiry: now + MAX_AGE_MS };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    // Storage disabled — nothing to record.
  }
};

// Exported for the tests, so a change to the window is caught by its own
// assertion rather than by arithmetic repeated in the test.
export const DISMISSAL_WINDOW_MS = MAX_AGE_MS;
export const DISMISSAL_STORAGE_KEY = STORAGE_KEY;
